/**
 * 画像リサイズ（Web ポスト投稿用）。
 *
 * ブラウザで選んだ画像を Canvas で長辺上限までリサイズし、JPEG の data URL を返す。
 * API へは base64（data URL）で送るため、送信前に縮小してペイロードを小さく保つ
 * （サーバー側上限は約2MB・デコード後バイト数）。
 */

/** 長辺の上限（px）。これを超える画像は等比縮小する。 */
const MAX_EDGE = 1280;
/** JPEG 品質（0〜1）。 */
const QUALITY = 0.82;

/**
 * File（画像）を読み込み、長辺 MAX_EDGE 以内に縮小した JPEG の data URL を返す。
 * 画像以外や読み込み失敗時は reject。
 */
export async function resizeImageToDataUrl(file: File): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new Error('not_an_image');
  }

  const bitmapUrl = URL.createObjectURL(file);
  try {
    const img = await loadImage(bitmapUrl);
    const { width, height } = fitWithin(img.naturalWidth, img.naturalHeight, MAX_EDGE);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('canvas_unavailable');
    }
    ctx.drawImage(img, 0, 0, width, height);
    return canvas.toDataURL('image/jpeg', QUALITY);
  } finally {
    URL.revokeObjectURL(bitmapUrl);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image_load_failed'));
    img.src = src;
  });
}

/** 元サイズを維持しつつ長辺を maxEdge 以内に収める幅・高さを返す。 */
function fitWithin(
  w: number,
  h: number,
  maxEdge: number,
): { width: number; height: number } {
  if (w <= maxEdge && h <= maxEdge) {
    return { width: w, height: h };
  }
  const scale = maxEdge / Math.max(w, h);
  return {
    width: Math.round(w * scale),
    height: Math.round(h * scale),
  };
}
